import { useAtomValue } from 'jotai/index';
import { FileUpload } from 'primereact/fileupload';
import { useRef } from 'react';
import {
    MemberAvatar,
    Spinner,
    SpinnerSize,
    useToastController,
    View,
    XStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { adminUserAtom } from '../../../entities/entityAtoms';
import { type MemberDetails } from '../../../entities/entityTypes';
import { useMemberImage } from '../../hooks/useMemberImage';
import { useUploadMemberImage } from '../../hooks/useUploadMemberImage';
import { validateImageFile } from '../../utils/memberImageUtils';

export const MemberAvatarRenderer = ({
    id,
    firstName,
    lastName,
}: MemberDetails) => {
    const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`;

    const adminUser = useAtomValue(adminUserAtom);
    const canUploadMemberImage =
        adminUser.authorization.selectedMembership.general.canEdit;

    const fileUploadRef = useRef<FileUpload>(null);
    const toast = useToastController();

    const { mutateAsync: uploadImage, isPending } = useUploadMemberImage();

    const { imageUrl } = useMemberImage(id);

    const handleFileSelect = async (event: { files: File[] }) => {
        const file = event.files[0];
        if (!file) return;

        const validation = validateImageFile(file);
        if (!validation.valid) {
            toast.show(validation.error as string, MutationErrorToastOptions());
            fileUploadRef.current?.clear();
            return;
        }

        try {
            await uploadImage({ userId: id, file });
            toast.show('Profile image uploaded!', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error uploading image', MutationErrorToastOptions());
        }

        fileUploadRef.current?.clear();
    };

    return (
        <XStack gap={8} alignItems="center">
            <View position="relative">
                <MemberAvatar
                    imageSrc={imageUrl ?? undefined}
                    text={initials}
                    avatarSize="medium"
                    withBorder
                />
                {isPending && (
                    <View
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        backgroundColor="rgba(0,0,0,0.5)"
                        borderRadius={100}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Spinner size={SpinnerSize.SMALL} />
                    </View>
                )}
                {canUploadMemberImage && !isPending && (
                    <FileUpload
                        ref={fileUploadRef}
                        mode="basic"
                        accept="image/jpeg,image/jpg,image/png"
                        auto
                        chooseLabel=""
                        onSelect={handleFileSelect}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer',
                            zIndex: 1,
                        }}
                    />
                )}
            </View>
        </XStack>
    );
};
